import ApiService from '../services/api';
import WebSocketService from '../services/websocket';
import { debugLog } from '../debug';
import { t } from '../i18n';

export default class PushManager {
    constructor(appId, clientId, fp = null) {
        this.appId = appId;
        this.clientId = clientId;
        this.fp = fp; // fingerprint for API validation
        this.swRegistration = null;
        this.isSupported = 'serviceWorker' in navigator && 'PushManager' in window;
        // 为什么不可用 —— 通知卡片据此显示三种完全不同的说法，不再一律塌缩成
        // 「不可用」：
        //   'browser'  浏览器真的没有 serviceWorker / PushManager
        //   'no_sw'    当前站点根目录没有可用的 sw-lane.js（第三方集成未部署）
        //   null       可用
        this.unsupportedReason = this.isSupported ? null : 'browser';
    }

    swPath() {
        const explicit = (typeof window.laneChatSwPath === 'string' && window.laneChatSwPath) ? window.laneChatSwPath : null;
        return explicit || '/sw-lane.js';
    }

    /**
     * 「当前 origin 下有没有一份能用的 sw-lane.js」。
     *
     * 分两档，是为了同时满足两个互相拉扯的要求：
     *   (a) 我们自己的域名上推送必须可用；
     *   (b) 没部署 SW 的第三方商户站点，控制台不许被我们刷红。
     * HEAD 探测虽然不会抛异常，但 404 仍会在控制台留一条红色的
     * "Failed to load resource" —— 所以只在访客真的去点推送那一行时才打这个请求。
     *
     * @param {boolean} allowNetwork 允许发探测请求（卡片渲染时传 false，点击时传 true）
     * @returns {Promise<boolean|null>} true/false = 已确定；null = 还不知道（没许可上网探）
     */
    async ensureSupportKnown(allowNetwork = true) {
        if (!this.isSupported) return false;              // 浏览器就没这两个 API
        if (this._swKnown !== undefined) return this._swKnown;

        // 零请求的判定 1：sessionStorage 里这个 origin 的历史结论
        const cacheKey = 'lane_sw_ok';
        try {
            const cached = sessionStorage.getItem(cacheKey);
            if (cached === '1' || cached === '0') {
                this._swKnown = cached === '1';
                if (!this._swKnown) { this.isSupported = false; this.unsupportedReason = 'no_sw'; }
                return this._swKnown;
            }
        } catch (e) { /* 隐私模式下不可用，忽略 */ }

        // 零请求的判定 2：本 origin 已经注册过 SW（回访 / 之前订阅过）→ 一定可用
        try {
            const reg = await navigator.serviceWorker.getRegistration();
            if (reg) {
                this._swKnown = true;
                try { sessionStorage.setItem(cacheKey, '1'); } catch (e) {}
                return true;
            }
        } catch (e) { /* ignore */ }

        if (!allowNetwork) return null;                    // 还不确定，但现在不许发请求

        // 到这一步才发探测请求（访客已经表达了使用推送的意图）
        let ok = false;
        try {
            const probe = await fetch(this.swPath(), { method: 'HEAD', cache: 'no-store' });
            if (probe.status === 405 || probe.status === 501) {
                ok = true;                                  // 服务器不认 HEAD，交给 register() 判
            } else {
                const ct = (probe.headers.get('content-type') || '').toLowerCase();
                // 200 但 content-type 是 html = SPA 的 index.html 兜底
                // （dash.lane.chat 就是这样：200 text/html），不是真 SW，
                // 直接注册会以 MIME 类型错误失败。
                ok = probe.ok && /javascript|ecmascript/.test(ct);
            }
        } catch (e) {
            ok = false;
        }
        this._swKnown = ok;
        try { sessionStorage.setItem(cacheKey, ok ? '1' : '0'); } catch (e) {}
        if (!ok) { this.isSupported = false; this.unsupportedReason = 'no_sw'; }
        return ok;
    }

    // 初始化Service Worker
    async init() {
        if (!this.isSupported) {
            debugLog('Push notifications not supported');
            return false;
        }

        // 这里问的必须是「当前 origin 下有没有一份能用的 SW 文件」。
        //
        // 旧实现问的是「API 是否与页面同源」（serverOrigin === location.origin），
        // 这是两个不相干的问题 —— service worker 只要求从页面自己的 origin 提供，
        // API 在哪个域无关。结果是把第一方场景整个误杀：在 lane.chat 上
        // serverOrigin 是 api.lane.chat，永远不等于 location.origin，于是浏览器
        // 推送被自己的守卫关死；把 API 放在 api.* 子域的商户（绝大多数）同样中招。
        //
        // 现在交给 ensureSupportKnown() 做真实文件探测（HEAD + content-type），
        // 探不到就静默降级到邮件/Telegram，不在商户控制台刷红色报错。
        const swPath = this.swPath();
        if (!(await this.ensureSupportKnown(true))) {
            debugLog('[Push] No usable service worker at', swPath, '— falling back to email/Telegram.');
            return false;
        }

        try {
            this.swRegistration = await navigator.serviceWorker.register(swPath);
            debugLog('[Push] Service Worker registered');

            // 等待Service Worker激活
            await navigator.serviceWorker.ready;

            return true;
        } catch (error) {
            // 用 warn 而不是 error：SW 文件缺失属于可预期的集成环境问题，
            // 不该在商户控制台里刷红色报错。
            console.warn('[Push] Service Worker registration failed:', error && error.message ? error.message : error);
            this.isSupported = false;
            this.unsupportedReason = 'no_sw';
            return false;
        }
    }

    // 检查当前订阅状态
    async checkSubscription() {
        if (!this.swRegistration) {
            const initialized = await this.init();
            if (!initialized || !this.swRegistration) {
                return false;
            }
        }

        try {
            const subscription = await this.swRegistration.pushManager.getSubscription();
            return subscription !== null;
        } catch (error) {
            console.error('Failed to check subscription:', error);
            return false;
        }
    }

    // 获取VAPID公钥
    async getVapidPublicKey() {
        try {
            const data = await ApiService.getPushConfig(this.appId, this.fp);
            
            if (data.success && data.public_key) {
                return this.urlBase64ToUint8Array(data.public_key);
            }
            
            throw new Error('Failed to get VAPID public key');
        } catch (error) {
            console.error('Failed to get VAPID key:', error);
            throw error;
        }
    }

    // 订阅推送通知
    async subscribe() {
        if (!this.swRegistration) {
            const initialized = await this.init();
            if (!initialized || !this.swRegistration) {
                // 尝试使用已有的 service worker
                if ('serviceWorker' in navigator) {
                    try {
                        this.swRegistration = await navigator.serviceWorker.ready;
                    } catch (e) {
                        console.warn('[Push] No service worker available:', e);
                    }
                }

                if (!this.swRegistration) {
                    throw new Error(t('binding.push_unavailable'));
                }
            }
        }

        try {
            // 请求通知权限
            const permission = await this.requestPermission();
            if (permission !== 'granted') {
                throw new Error('Permission denied');
            }

            // 获取VAPID公钥
            const vapidPublicKey = await this.getVapidPublicKey();

            // 订阅推送
            const subscription = await this.swRegistration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: vapidPublicKey
            });

            // 获取当前页面URL（父窗口的URL）
            let currentUrl = window.location.href;
            // 如果在iframe中，尝试获取父窗口的URL
            if (window.parent !== window) {
                try {
                    currentUrl = window.parent.location.href;
                } catch (e) {
                    // 跨域情况下无法访问父窗口URL，使用referrer
                    currentUrl = document.referrer || window.location.href;
                }
            }

            // 发送订阅信息到服务器
            const result = await ApiService.subscribePush(
                subscription.toJSON(),
                this.appId,
                this.clientId,
                this.fp
            );
            
            if (result.success) {
                debugLog('Push subscription successful');
                
                // 通过WebSocket更新推送绑定状态
                if (WebSocketService.isConnected) {
                    WebSocketService.send({
                        type: 'binding_update',
                        client_id: this.clientId,
                        binding_type: 'push',
                        status: 'success',
                        message: t('binding.push_enabled_msg'),
                        source_url: currentUrl
                    });
                }

                // 不再往对话流追加一张"已启用"卡片：状态由 NotificationCard 原地显示。
                // （旧行为是每次授权都多一条消息，同一件事被拆成两张风格不一致的卡。）
                
                return true;
            }
            
            throw new Error(result.message || 'Subscription failed');
        } catch (error) {
            console.error('Push subscription failed:', error);
            throw error;
        }
    }

    // 取消订阅
    async unsubscribe() {
        if (!this.swRegistration) {
            return false;
        }

        try {
            const subscription = await this.swRegistration.pushManager.getSubscription();
            
            if (subscription) {
                await subscription.unsubscribe();
                
                // 通知服务器
                await ApiService.unsubscribePush(
                    subscription.endpoint,
                    this.appId,
                    this.clientId
                );
                
                debugLog('Push unsubscription successful');
                
                // 通过WebSocket更新推送绑定状态
                if (WebSocketService.isConnected) {
                    WebSocketService.send({
                        type: 'binding_update',
                        client_id: this.clientId,
                        binding_type: 'push',
                        status: 'unsubscribed',
                        message: t('binding.push_disabled_msg')
                    });
                }

                // 同上：关闭结果也由 NotificationCard 原地显示，不再追加消息。
                
                return true;
            }
            
            return false;
        } catch (error) {
            console.error('Push unsubscription failed:', error);
            return false;
        }
    }

    // 请求通知权限
    async requestPermission() {
        if (!('Notification' in window)) {
            throw new Error('Notifications not supported');
        }

        if (Notification.permission === 'granted') {
            return 'granted';
        }

        if (Notification.permission === 'denied') {
            throw new Error('Notifications permission denied');
        }

        // 请求权限
        const permission = await Notification.requestPermission();
        return permission;
    }

    // 测试推送通知
    async testNotification() {
        try {
            const subscription = await this.swRegistration.pushManager.getSubscription();
            if (!subscription) {
                throw new Error('No subscription found');
            }
            const result = await ApiService.testPush(subscription.toJSON());
            return result.success;
        } catch (error) {
            console.error('Test notification failed:', error);
            return false;
        }
    }

    // 工具函数：将base64转换为Uint8Array
    urlBase64ToUint8Array(base64String) {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding)
            .replace(/\-/g, '+')
            .replace(/_/g, '/');

        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);

        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        
        return outputArray;
    }

    // 获取订阅状态详情
    async getSubscriptionDetails() {
        try {
            const subscription = await this.swRegistration.pushManager.getSubscription();
            const permission = Notification.permission;
            
            return {
                isSubscribed: subscription !== null,
                permission: permission,
                endpoint: subscription ? subscription.endpoint : null,
                isSupported: this.isSupported
            };
        } catch (error) {
            return {
                isSubscribed: false,
                permission: 'default',
                endpoint: null,
                isSupported: this.isSupported,
                error: error.message
            };
        }
    }
    
    // 显示推送订阅卡片（先检查服务器端订阅状态）
    // showPushCard() 已删除（2026-08 合并）：它弹的是那张 2px 主色边框 + 实心
    // 主色按钮的独立推送卡（商户主色是红色时整张卡血红，像报错），并且"已订阅"
    // 时直接 return —— 点了毫无反馈。现在统一由 NotificationCard 承载，
    // PushManager 只保留纯操作（subscribe / unsubscribe / checkSubscription）。
}
